import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  FlatSegment,
  InsightsBookmarkParams,
  InsightsLegendUrlData,
  InsightsQueryResults,
} from 'common/report/util/insights/types';
import {useStore} from 'common/util/component-helpers';
import {currentProjectStore, ProjectDetails, ProjectTimezone} from 'common/report/stores/project-details';
import {getDisplayOptions, memoizedGetTimestamp, showHighCardinality} from 'common/report/util/insights/utils';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {
  computeMetricClauseByKey,
  LineChartTooltipStat,
  RenderLineChartTooltipParams,
  ShowClauseData,
} from 'common/report/util/insights/tooltip';
import {formatDate, sortIsoDates} from 'common/util/date';
import {PopoverCallback} from 'common/charts/types';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getWorkspaceId} from 'common/report/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  CSV_EXPORT_LIMIT,
  DEFAULT_SELECTED_SEGMENTS_LIMIT,
  MAX_SELECTED_SEGMENTS_LIMIT,
  OLD_DEFAULT_SELECTED_SEGMENTS_LIMIT,
  OLD_MAX_SELECTED_SEGMENTS_LIMIT,
  ReportName,
} from 'common/report/constants';

import {getLineChartData, LineChartData} from './util';
import {template} from './template';
import css from './index.styl?inline';

interface InsightsLineChartState {
  lineChartData: null | LineChartData;
  /** clicked highcharts point object to display a popover for */
  popoverSelection: TODO | null;
  /** selected series & point indices */
  popoverSelectionIndex: [seriesIndex: number, pointIndex: number] | null;
  /** holds positioning of the popover for the active highcharts point */
  popoverStyles: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
}

interface InsightsLineChartJsxProps {
  attrs: {
    loaded: boolean;
    failed: boolean;
    'show-annotations': boolean;
    'utc-offset': number;
    timezone: string;
  };
  props: {
    bookmarkParams: InsightsBookmarkParams;
    results: InsightsQueryResults;

    // legend data will eventually be persisted in bookmark, URL persistence is a stopgap
    urlLegendData: InsightsLegendUrlData;
    popoverCalback?: PopoverCallback;
  };
}

// TODO ongoing: migrating Insights' line chart functionality into this common widget to be shared between Insights & Dashboards
export class InsightsLineChart extends Component<
  InsightsLineChartState,
  InsightsLineChartJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InsightsLineChartJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      loaded: `boolean`,
      failed: `boolean`,
      'show-annotations': `boolean`,
      'utc-offset': `number`,
      timezone: `string`,
    };
  }

  protected project: ProjectDetails | null = null;
  protected projectTimezone: ProjectTimezone | null = null;
  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  private _dataGroups: Record<string, DataGroup> | null = null;

  private _bookmarkParams: InsightsBookmarkParams | null = null;
  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }
  public set bookmarkParams(value: InsightsBookmarkParams | null) {
    if (!isEqual(this._bookmarkParams, value)) {
      this._bookmarkParams = value;
      this.updateLineChartData();
    }
  }

  private _results: InsightsQueryResults | null = null;
  public get results(): InsightsQueryResults | null {
    return this._results;
  }
  public set results(value: InsightsQueryResults | null) {
    if (!isEqual(this._results, value)) {
      this._results = value;
      this.updateLineChartData();
    }
  }

  private _urlLegendData: InsightsLegendUrlData | null = null;
  public get urlLegendData(): InsightsLegendUrlData | null {
    return this._urlLegendData;
  }
  public set urlLegendData(value: InsightsLegendUrlData | null) {
    this._urlLegendData = value;
    this.updatePersistedLegendSelections();
  }

  protected get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public popoverCallback: PopoverCallback | null = null;

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        lineChartData: null,
        popoverStyles: null,
        popoverSelection: null,
        popoverSelectionIndex: null,
      },
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: showHighCardinality()
            ? DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Insights]
            : OLD_DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Insights],
          selectAllLimit: showHighCardinality()
            ? MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Insights]
            : OLD_MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Insights],
        }),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    // this component disables project & dataGroup Stores if equivalent attributes are specified, this may be required
    // in environments where Stores don't work, eg. Public Dashboards
    if (!this._dataGroups && !this.hasProjectTimeAttributes()) {
      useStore(this, currentProjectStore, () => {
        if (this.project?.id !== currentProjectStore.value.id) {
          const projectId = currentProjectStore.value.id;
          const workspaceId = parseInt(getWorkspaceId(), 10);
          // Note: data groups are only used in tooltips and (as of writing) do not need to trigger state updates/renders or annotated with people properties
          dataGroupStore.fetch({projectId, workspaceId}); // async, no need to wait
        }
        if (!isEqual(this.project, currentProjectStore.value)) {
          this.project = currentProjectStore.value;
          this.projectTimezone = {
            utcOffset: currentProjectStore.value.utcOffset,
            timezone: currentProjectStore.value.timezone,
          };
          this.updateLineChartData();
        }
      }).fetch();
    }
    this.updateLineChartData();
    this.updatePersistedLegendSelections();
  }

  protected hasProjectTimeAttributes(): boolean {
    return Boolean(this.attr(`timezone`).length);
  }

  protected hasRequiredParams(): boolean {
    // Props may have been set before connectedCallback() fires, therefore need to ensure
    // this component is connected before accessing a context (e.g. segmentFilterController)
    return !!(this.isConnected && this.getProjectTimezone() && this.bookmarkParams && this.results);
  }

  protected getProjectTimezone(): ProjectTimezone | null {
    if (this.hasProjectTimeAttributes()) {
      return {
        utcOffset: this.attr(`utc-offset`),
        timezone: this.attr(`timezone`),
      };
    }
    return this.projectTimezone;
  }

  private updateLineChartData(): void {
    if (this.hasRequiredParams()) {
      const lineChartData = getLineChartData(this.getProjectTimezone(), this.bookmarkParams, this.results);
      this.update({lineChartData});

      const {meta} = this.results;
      const queryLimits = meta.queryLimits;

      let apiResultsTruncated = false;
      if (meta.report_sections.show) {
        apiResultsTruncated = some(meta.report_sections.show, `apiResultsTruncated`);
      }

      this.segmentFilterController.loadSections([
        {
          items: lineChartData.legendSegmentItems,
          metadata: lineChartData.legendIdToIndexMap, // Insights app is expected to use this mapping to persist legend selection indices to the URL
          apiResultsTruncated,
        },
      ]);

      this.segmentFilterController.segmentationQueryLimit = queryLimits?.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = CSV_EXPORT_LIMIT[ReportName.Insights];
    } else {
      this.update({lineChartData: null});
    }
  }

  private updatePersistedLegendSelections() {
    const {lineChartData} = this.state;
    const indexSelections = this.urlLegendData?.idxSelections;

    if (
      this.state.lineChartData &&
      Array.isArray(indexSelections) &&
      indexSelections.length > 0 &&
      !Array.isArray(indexSelections[0])
    ) {
      const selectedIds = (indexSelections as number[]).map(
        (legendIndex) => lineChartData.legendIndexToId[legendIndex],
      );

      this.segmentFilterController.loadPersistedSelections([selectedIds]);
    }
  }

  protected generatePointTooltipParams(highchartsContext: {
    point: TODO;
    series: TODO;
    percentage: number;
  }): RenderLineChartTooltipParams {
    const {point, series, percentage} = highchartsContext;
    const segmentKey = series.options.name; // use name from options because highcharts randomly names series with an empty string name
    const mainSegment: FlatSegment = this.state.lineChartData.segmentLookup.get(segmentKey);

    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups() ?? {}),
    });

    const mainDates = sortIsoDates(Object.keys(mainSegment.value));
    const value = mainSegment.value[mainDates[point.index]];
    const stat: LineChartTooltipStat = {
      timestamp: point.x,
      value,
      dateRange: {
        fromDate: this.results.date_range.from_date,
        toDate: this.results.date_range.to_date,
      },
    };

    const showPreviousValueDelta = !Number.isFinite(percentage) && point.index > 0;
    const deltaStat: LineChartTooltipStat = showPreviousValueDelta
      ? {
          value: mainSegment.value[mainDates[point.index - 1]],
        }
      : null;

    let timeComparisonStat: null | LineChartTooltipStat = null;
    if (this.state.lineChartData.timeComparisonSegmentLookup.has(segmentKey)) {
      const timeComparisonSegment: FlatSegment = this.state.lineChartData.timeComparisonSegmentLookup.get(segmentKey);
      const comparisonDates = sortIsoDates(Object.keys(timeComparisonSegment.value));
      const timeComparisonValue = timeComparisonSegment.value[comparisonDates[point.index]];

      const timeComparisonTimestamp = memoizedGetTimestamp(
        comparisonDates[point.index],
        this.getProjectTimezone().timezone,
        this.getProjectTimezone().utcOffset,
      );
      timeComparisonStat = {
        value: timeComparisonValue ?? 0,
        timestamp: timeComparisonTimestamp,
        dateRange: {
          fromDate: this.results.time_comparison.date_range.from_date,
          toDate: this.results.time_comparison.date_range.to_date,
        },
      };
    }

    const displayOptions = getDisplayOptions(this.bookmarkParams.sections, this.bookmarkParams.displayOptions);
    const isFirstInterval = point.index === 0;
    const isLastInterval = point.index === series.data.length - 1;

    return {
      metricClauseData: metricClauseByKey[mainSegment.path[0]],
      segments: mainSegment.path.slice(1), // this populates the breakdown subheading, exclude the primary metric in the segment array
      stat,
      deltaStat,
      timeComparisonStat,
      percent: percentage,
      timeUnit: displayOptions.timeUnit,
      isFirstInterval,
      isLastInterval,
      titleColor: series.options.color,
      utcOffset: this.getProjectTimezone().utcOffset,
      isComparisonItem: series.userOptions.isComparisonItem,
    };
  }

  private getDataGroups(): Record<string, DataGroup> {
    if (this._dataGroups) {
      return this._dataGroups;
    } else {
      return dataGroupStore.value;
    }
  }

  getPointDataFromSelection() {
    if (this.state.popoverSelection === null) {
      return null;
    }

    const point = this.state.popoverSelection;
    return this.generatePointTooltipParams({point, series: point.series, percentage: point.percentage});
  }

  handlePointClick(chartSeries: TODO, pointIdx: number, plotX: number, plotY: number) {
    const point = chartSeries.data[pointIdx];
    const pointData = this.generatePointTooltipParams({point, series: chartSeries, percentage: point.percentage});
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;

    // const parentRect = this.getBoundingClientRect();
    const left = chartSeries.chart.plotLeft + plotX + 12;
    const top = chartSeries.chart.plotTop + plotY + 12;

    if (this.popoverCallback?.call(null, metricKey, pointData.segments, point).length) {
      point.select();
      chartSeries.chart.renderTo.style.pointerEvents = `none`;
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSelectionIndex: [chartSeries.index, pointIdx],
        popoverSelection: {...point},
      });
    }
  }

  getPopoverAttrs() {
    const pointData = this.getPointDataFromSelection();
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;

    return {
      open: true,
      title: metricKey,
      subtitle: formatDate(pointData.stat.timestamp, {unit: pointData.timeUnit}),
      'button-options': this.popoverCallback(metricKey, pointData.segments, this.state.popoverSelection),
    };
  }

  handlePopoverAction(ev: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {...ev.detail, pointData: this.getPointDataFromSelection()},
      }),
    );
    this.handlePopoverClose();
  }
  handlePopoverClose() {
    this.state.popoverSelection.series.chart.renderTo.style.pointerEvents = ``;
    this.update({popoverSelection: null, popoverStyles: null, popoverSelectionIndex: null});
  }

  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }

  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
    this.updateLineChartData();
  }
}

export const InsightsLineChartEl = defineAndMakeJsxFactory<InsightsLineChartJsxProps>(
  `mp-insights-line-chart`,
  InsightsLineChart,
);
